'use client';

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import {
  authenticatedUser,
  SessionUser,
  storeAuthSession,
  consumeNewSignupFlag,
  AuthResultSuccess,
} from '@/app/utils/authentication';
import { useTracking } from '@/components/tracking-provider';
import {
  PlanTier,
  PlanConfig,
  PLANS,
  parseTierParam,
  parsePeriodParam,
  formatPrice,
  calculateTotalPrice,
} from '@/app/config/plans';
import { getCheckoutExperimentSuperProp } from '@/app/utils/checkout-tracking';
import { useAuthSystem } from '@/app/treatments-provider';
import type { AuthSessionInfo } from '@/app/api/auth/_lib/types';

// ============ Types ============

export type CheckoutStep = 'loading' | 'plan' | 'auth' | 'payment' | 'success';

interface CheckoutState {
  step: CheckoutStep;
  selectedTier: PlanTier | null;
  selectedYears: number;
  user: SessionUser | null;
  isProcessing: boolean;
  error: string | null;
  authMode: 'signup' | 'login';
  hasInitialTier: boolean;
  /** Source identifier from query param, used for legacy tracking */
  source: string | null;
}

type CheckoutMessageType =
  | 'checkout:ready'
  | 'checkout:authenticated'
  | 'checkout:success'
  | 'checkout:close'
  | 'checkout:error';

interface CheckoutMessage {
  type: CheckoutMessageType;
  payload?: Record<string, unknown>;
}

export interface CheckoutContextValue {
  // State
  state: CheckoutState;
  selectedPlan: PlanConfig | undefined;
  displayPrice: string;
  isEmbedded: boolean;
  successUrl: string;
  modalWidthClass: string;
  authSystem: 'legacy' | 'modern';

  // Actions
  goToStep: (step: CheckoutStep) => void;
  selectPlan: (tier: PlanTier) => void;
  handleAuthSuccess: (result: AuthResultSuccess) => void;
  handleModernAuthSuccess: (session: AuthSessionInfo) => void;
  handleClose: () => void;
  handlePaymentSuccess: (result: AuthResultSuccess) => void;
  handleChangePlan: () => void;
  toggleAuthMode: () => void;
  setYears: (years: number) => void;
  setError: (error: string | null) => void;
  setProcessing: (processing: boolean) => void;
}

// ============ Context ============

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

// ============ Hook ============

export function useCheckout(): CheckoutContextValue {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}

// ============ Helpers ============

/**
 * Convert AuthResultSuccess user to SessionUser format
 * Handles type differences between API response and local session types
 */
function authResultUserToSessionUser(result: AuthResultSuccess): SessionUser {
  const user = result.access_token.user;
  return {
    id: user.id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    has_mobile: user.has_mobile,
    is_pro: user.is_pro,
    is_pro_plus: user.is_pro_plus,
    pro_membership: user.pro_membership
      ? {
          id: user.pro_membership.id,
          package_name: user.pro_membership.package_name,
          renewal_date:
            user.pro_membership.renewal_date instanceof Date
              ? user.pro_membership.renewal_date.toISOString()
              : String(user.pro_membership.renewal_date),
          renewal_date_i: user.pro_membership.renewal_date_i,
          subscription_length: user.pro_membership.subscription_length,
        }
      : undefined,
  };
}

function useIsEmbedded(): boolean {
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const embedParam = urlParams.get('embedded') === 'true';
    const inIframe = window.self !== window.top;
    setIsEmbedded(embedParam || inIframe);
  }, []);

  return isEmbedded;
}

function sendParentMessage(message: CheckoutMessage): void {
  if (typeof window === 'undefined') return;
  if (window.parent === window) return;

  try {
    window.parent.postMessage(message, window.location.origin);
  } catch (error) {
    console.error('Failed to send postMessage:', error);
  }
}

/**
 * Get the appropriate modal width class for each step
 */
function getModalWidthClass(step: CheckoutStep): string {
  switch (step) {
    case 'loading':
      return 'max-w-md'; // Compact for loading spinner
    case 'plan':
      return 'max-w-5xl'; // Wider for 3 plan cards
    case 'auth':
      return 'max-w-lg'; // Narrower for form
    case 'payment':
      return 'max-w-xl'; // Medium for payment form
    case 'success':
      return 'max-w-lg'; // Narrower for success message
    default:
      return 'max-w-xl';
  }
}

// ============ Provider ============

interface CheckoutProviderProps {
  children: ReactNode;
  initialTier?: string;
  initialPeriod?: string;
  initialSource?: string;
  successUrl?: string;
  onClose?: () => void;
  onSuccess?: () => void;
  embedded?: boolean;
}

export function CheckoutProvider({
  children,
  initialTier,
  initialPeriod,
  initialSource,
  successUrl = '/journals',
  onClose,
  onSuccess,
  embedded = false,
}: CheckoutProviderProps) {
  const tracker = useTracking();
  const authSystem = useAuthSystem();
  // Use prop for embedded state, with hook as fallback for auto-detection
  const isEmbeddedFromHook = useIsEmbedded();
  const isEmbedded = embedded || isEmbeddedFromHook;

  // Parse initial tier and period from URL params if provided
  const parsedTier = parseTierParam(initialTier ?? null);
  const parsedYears = parsePeriodParam(initialPeriod ?? null);
  const hasInitialTier = !!parsedTier;
  const parsedSource = initialSource ?? null;

  // Initialize state
  // For embedded mode: start at 'loading' to avoid SSR/client hydration mismatch
  // For modal mode: compute initial state immediately (no SSR concern)
  const [state, setState] = useState<CheckoutState>(() => {
    if (isEmbedded) {
      // Embedded/iframe mode - defer auth check to useEffect
      return {
        step: 'loading',
        selectedTier: parsedTier || null,
        selectedYears: parsedYears,
        user: null,
        isProcessing: false,
        error: null,
        authMode: 'signup',
        hasInitialTier,
        source: parsedSource,
      };
    }

    // Modal mode - safe to check auth immediately (client-side only)
    // A checkout surface consumes exactly one globally selected auth system.
    // The modern treatment never consults the legacy localStorage session.
    const user =
      authSystem === 'legacy' && typeof window !== 'undefined' ? authenticatedUser() : null;
    const isAuthenticated = !!user;

    let startStep: CheckoutStep = 'plan';
    if (hasInitialTier) {
      startStep = isAuthenticated ? 'payment' : 'auth';
    }

    return {
      step: startStep,
      selectedTier: parsedTier || null,
      selectedYears: parsedYears,
      user,
      isProcessing: false,
      error: null,
      authMode: 'signup',
      hasInitialTier,
      source: parsedSource,
    };
  });

  // Destination for the post-purchase CTA. Defaults to the provided successUrl,
  // and is reassigned to /welcome in handlePaymentSuccess when the user signed up
  // during this session (via the header signup or the checkout auth step).
  const [resolvedSuccessUrl, setResolvedSuccessUrl] = useState(successUrl);

  // For embedded mode: determine actual starting step after mount
  useEffect(() => {
    // Only run for embedded mode when in loading state
    if (!isEmbedded || state.step !== 'loading') return;

    const user = authSystem === 'legacy' ? authenticatedUser() : null;
    const isUserAuthenticated = !!user;

    let startStep: CheckoutStep = 'plan';
    if (hasInitialTier) {
      startStep = isUserAuthenticated ? 'payment' : 'auth';
    }

    setState((prev) => ({
      ...prev,
      step: startStep,
      user,
    }));

    // Notify parent that checkout is ready and initialized
    sendParentMessage({
      type: 'checkout:ready',
      payload: {
        step: startStep,
        isAuthenticated: isUserAuthenticated,
        selectedTier: parsedTier,
      },
    });
  }, [authSystem, isEmbedded, state.step, hasInitialTier, parsedTier]);

  // Derived values
  const selectedPlan: PlanConfig | undefined = state.selectedTier
    ? PLANS[state.selectedTier]
    : undefined;

  const displayPrice = selectedPlan
    ? formatPrice(calculateTotalPrice(state.selectedTier!, state.selectedYears))
    : '';

  const modalWidthClass = getModalWidthClass(state.step);

  // Track checkout begin on mount and register A/B experiment super property
  useEffect(() => {
    // Register checkout iframe A/B experiment super property when embedded
    if (isEmbedded) {
      tracker.setSuperProps(getCheckoutExperimentSuperProp());
    }

    tracker.trackEvent('Checkout Begin', {
      tier: state.selectedTier,
      source: isEmbedded ? 'iframe' : 'modal',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ Actions ============

  const goToStep = useCallback((step: CheckoutStep) => {
    setState((prev) => ({ ...prev, step, error: null }));
  }, []);

  const selectPlan = useCallback(
    (tier: PlanTier) => {
      setState((prev) => ({ ...prev, selectedTier: tier }));
      tracker.trackEvent('Checkout Plan Selected', { tier });

      // Move to next step
      if (authSystem === 'legacy' && state.user) {
        goToStep('payment');
      } else {
        goToStep('auth');
      }
    },
    [authSystem, state.user, goToStep, tracker],
  );

  const handleAuthSuccess = useCallback(
    (result: AuthResultSuccess) => {
      const sessionUser = authResultUserToSessionUser(result);
      setState((prev) => ({
        ...prev,
        user: sessionUser,
        step: 'payment',
      }));

      tracker.trackEvent('Checkout Auth Complete', {
        method: state.authMode,
        tier: state.selectedTier,
      });

      if (isEmbedded) {
        sendParentMessage({
          type: 'checkout:authenticated',
          payload: { userId: sessionUser.id },
        });
      }
    },
    [state.authMode, state.selectedTier, tracker, isEmbedded],
  );

  const handleModernAuthSuccess = useCallback(
    (session: AuthSessionInfo) => {
      setState((prev) => ({ ...prev, user: session.user }));

      tracker.trackEvent('Checkout Auth Complete', {
        method: state.authMode,
        tier: state.selectedTier,
      });

      if (isEmbedded) {
        sendParentMessage({
          type: 'checkout:authenticated',
          payload: { userId: session.user.id },
        });
      }
    },
    [state.authMode, state.selectedTier, tracker, isEmbedded],
  );

  const handleClose = useCallback(() => {
    if (isEmbedded) {
      sendParentMessage({ type: 'checkout:close' });
    }
    onClose?.();
  }, [isEmbedded, onClose]);

  const handlePaymentSuccess = useCallback(
    (result: AuthResultSuccess) => {
      // Store the updated session
      storeAuthSession(result, tracker, 'Checkout Complete', {
        'pz-subscription-plan': state.selectedTier || '',
      });

      // Users who signed up during this session land on /welcome after purchase
      // instead of the default destination. consumeNewSignupFlag clears the flag
      // so it applies to this purchase only, and never throws into this success
      // path even when storage is unavailable.
      if (consumeNewSignupFlag()) {
        setResolvedSuccessUrl('/welcome');
      }

      setState((prev) => ({ ...prev, step: 'success' }));

      tracker.trackEvent('Checkout Complete', {
        tier: state.selectedTier,
        years: state.selectedYears,
      });

      if (isEmbedded) {
        sendParentMessage({
          type: 'checkout:success',
          payload: { tier: state.selectedTier },
        });
      }

      onSuccess?.();
    },
    [state.selectedTier, state.selectedYears, tracker, isEmbedded, onSuccess],
  );

  const handleChangePlan = useCallback(() => {
    if (state.hasInitialTier) {
      // If checkout was launched with a pre-selected tier, close the modal
      handleClose();
    } else {
      // Otherwise, navigate back to plan selection
      goToStep('plan');
    }
  }, [state.hasInitialTier, handleClose, goToStep]);

  const toggleAuthMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      authMode: prev.authMode === 'signup' ? 'login' : 'signup',
    }));
  }, []);

  const setYears = useCallback((years: number) => {
    setState((prev) => ({ ...prev, selectedYears: years }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setProcessing = useCallback((isProcessing: boolean) => {
    setState((prev) => ({ ...prev, isProcessing }));
  }, []);

  // ============ Context Value ============

  const contextValue: CheckoutContextValue = {
    // State
    state,
    selectedPlan,
    displayPrice,
    isEmbedded,
    successUrl: resolvedSuccessUrl,
    modalWidthClass,
    authSystem,

    // Actions
    goToStep,
    selectPlan,
    handleAuthSuccess,
    handleModernAuthSuccess,
    handleClose,
    handlePaymentSuccess,
    handleChangePlan,
    toggleAuthMode,
    setYears,
    setError,
    setProcessing,
  };

  return <CheckoutContext.Provider value={contextValue}>{children}</CheckoutContext.Provider>;
}
